/** Acronyms kept uppercase in job titles. */
const TITLE_ACRONYMS = new Set([
  "CEO",
  "CFO",
  "COO",
  "CTO",
  "CIO",
  "CMO",
  "CPO",
  "VP",
  "SVP",
  "EVP",
  "GM",
  "HR",
  "IT",
  "USA",
  "US",
  "II",
  "III",
  "IV",
]);

const TITLE_ALIASES: Record<string, string> = {
  "authorized signer for the borrower": "Authorized Signer",
  "authorized signer": "Authorized Signer",
  "owner / officer": "Owner & Officer",
  "officer / owner": "Owner & Officer",
  "owner/officer": "Owner & Officer",
  "officer/owner": "Owner & Officer",
  "president/owner": "President & Owner",
  "president / owner": "President & Owner",
  "managing member": "Managing Member",
  "member manager": "Managing Member",
  "member-manager": "Managing Member",
};

const RELATIONSHIP_TITLE_FALLBACK: Record<string, string> = {
  OWNER: "Owner",
  EXECUTIVE: "Executive",
  MANAGER: "Manager",
  BOARD_MEMBER: "Board Member",
  EMPLOYEE: "Employee",
};

const NAME_PARTICLES = new Set([
  "van",
  "von",
  "de",
  "del",
  "della",
  "di",
  "da",
  "la",
  "le",
  "du",
  "der",
]);

function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function isMostlyUppercase(value: string): boolean {
  const letters = value.replace(/[^a-zA-Z]/g, "");
  if (letters.length < 2) return false;
  const upper = letters.replace(/[^A-Z]/g, "").length;
  return upper / letters.length >= 0.8;
}

function capitalizeSegment(segment: string): string {
  if (!segment) return segment;
  const lower = segment.toLowerCase();

  if (TITLE_ACRONYMS.has(segment.toUpperCase())) {
    return segment.toUpperCase();
  }

  if (lower.startsWith("mc") && lower.length > 2) {
    return `Mc${lower.charAt(2).toUpperCase()}${lower.slice(3)}`;
  }
  if (lower.startsWith("mac") && lower.length > 3 && lower.charAt(3) !== " ") {
    return `Mac${lower.charAt(3).toUpperCase()}${lower.slice(4)}`;
  }
  if (
    (lower.startsWith("o'") || lower.startsWith("d'")) &&
    lower.length > 2
  ) {
    return `${lower.slice(0, 2)}${lower.charAt(2).toUpperCase()}${lower.slice(3)}`;
  }

  if (NAME_PARTICLES.has(lower)) return lower;

  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function formatNameToken(token: string): string {
  if (!token) return token;
  const letters = token.replace(/[^A-Za-z]/g, "");
  const hasLower = /[a-z]/.test(letters);
  const hasUpper = /[A-Z]/.test(letters);
  // Mixed case is intentional ("McDonald", "Leonardo"). Uniform case is not.
  if (hasLower && hasUpper) return token;
  return token
    .split("-")
    .map((part) =>
      part
        .split("'")
        .map((seg) => capitalizeSegment(seg))
        .join("'"),
    )
    .join("-");
}

/**
 * Normalizes person names for display — e.g. "RUSSELL MOTT" → "Russell Mott".
 * Leaves already-mixed-case names unchanged.
 */
export function formatPersonName(name: string | null | undefined): string {
  if (!name) return "";
  const trimmed = collapseWhitespace(name);
  if (!trimmed) return "";

  const tokens = trimmed.split(" ");
  const formatted = tokens.map(formatNameToken).join(" ");

  return formatted
    .split(" ")
    .map((token, index) => {
      if (index > 0 && NAME_PARTICLES.has(token.toLowerCase())) {
        return token.toLowerCase();
      }
      return token;
    })
    .join(" ");
}

function formatTitleToken(token: string): string {
  const stripped = token.replace(/[.,]/g, "");
  if (TITLE_ACRONYMS.has(stripped.toUpperCase())) {
    return stripped.toUpperCase() + token.slice(stripped.length);
  }
  return formatNameToken(token);
}

function normalizeTitleSeparators(title: string): string {
  return collapseWhitespace(
    title.replace(/\s*\/\s*/g, " & ").replace(/\s*\+\s*/g, " & "),
  );
}

function titleCaseTitle(title: string): string {
  return title
    .split(" ")
    .map((word) => {
      if (!word) return word;
      if (word === "&") return word;
      return formatTitleToken(word);
    })
    .join(" ");
}

/**
 * Standardizes job titles for display — separators, aliases, and capitalization.
 */
export function formatJobTitle(
  title: string | null | undefined,
  relationshipType?: string | null,
): string | null {
  const trimmed = title?.trim();
  if (!trimmed) {
    if (relationshipType && RELATIONSHIP_TITLE_FALLBACK[relationshipType]) {
      return RELATIONSHIP_TITLE_FALLBACK[relationshipType] ?? null;
    }
    return null;
  }

  let normalized = normalizeTitleSeparators(trimmed);
  const aliasKey = normalized.toLowerCase();

  if (TITLE_ALIASES[aliasKey]) {
    return TITLE_ALIASES[aliasKey];
  }

  if (aliasKey === "applicant" && relationshipType === "OWNER") {
    return "Owner";
  }

  if (
    aliasKey === "employee" &&
    relationshipType &&
    relationshipType !== "EMPLOYEE"
  ) {
    return RELATIONSHIP_TITLE_FALLBACK[relationshipType] ?? titleCaseTitle(normalized);
  }

  if (isMostlyUppercase(normalized)) {
    normalized = titleCaseTitle(normalized);
  }

  return normalized;
}

/** Higher score = better canonical name when merging duplicates. */
export function personNameQuality(name: string): number {
  let score = 0;
  if (!isMostlyUppercase(name)) score += 10;
  if (/['-]/.test(name)) score += 2;
  if (/\d/.test(name)) score -= 8;
  if (name.length > 60) score -= 5;
  return score;
}
